/**
 * Resource name link for appointment detail surfaces, with loading and fallback states.
 */

import React from 'react';
import { Link } from '@tanstack/react-router';
import { Typography, Space, Spin, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetResourceQuery } from '@/api/mcp-appointments';
import { ResourceTypeIcon } from '@/components/resources/ResourceTypeIcon';
import { getResourceDisplayName } from '@/utils/serviceResourceLinks';

const { Text } = Typography;

interface ResourceLinkProps {
  /** Resource whose settings page should open when the link is followed. */
  resourceId: string;
}

/** Loads resource metadata and links to its company settings page. */
export const ResourceLink: React.FC<ResourceLinkProps> = ({ resourceId }) => {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const { data: resource, isLoading, isError } = useGetResourceQuery(resourceId);

  if (isLoading) {
    return (
      <Space size="small">
        <Spin size="small" />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('appointmentComponents.resourceLink.loading')}
        </Text>
      </Space>
    );
  }

  if (isError || !resource) {
    return (
      <Text type="secondary" style={{ fontSize: '12px' }}>
        {resourceId.slice(0, 8)}...
      </Text>
    );
  }

  const resourceName = getResourceDisplayName(resource, t('appointmentComponents.resourceLink.unnamedResource'));

  return (
    <Link
      to="/manage/company-resources/$resourceId/settings"
      params={{ resourceId }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = token.colorPrimary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'inherit';
      }}>
      <Space size="small" style={{ fontSize: '12px' }}>
        <ResourceTypeIcon type={resource.type} />
        <Text style={{ fontSize: '12px' }}>{resourceName}</Text>
      </Space>
    </Link>
  );
};

